import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { getObjectProperty } from 'app/helpers/utils';

@Injectable({ providedIn: 'root' })
export class SwaggerImportService {

    constructor(private formBuilder: FormBuilder) {
    }

    mapOauth2SwaggerFlowTypes(flow: string) {
        if (flow == 'application') {
            return 'clientCredentials';
        } else if (flow == 'accessCode') {
            return 'authorizationCode';
        } else {
            return flow;
        }
    }
    getScopesFormArray(scopes: any): FormArray {
        let scopesFormArray = this.formBuilder.array([]);
        Object.keys(scopes || {}).forEach((k) => {
            if (!k.startsWith('x-')) {
                let s = scopes[k];
                scopesFormArray.push(this.formBuilder.group({
                    name: [k, Validators.required],
                    description: [s || '']
                }));
            }
        });

        return scopesFormArray;
    }
    getAuthsFormArray(securityDefinitions: any): FormArray {
        let authsFormArray: FormArray = this.formBuilder.array([]);
        Object.keys(securityDefinitions).forEach((authName) => {
            let s = securityDefinitions[authName];
            authsFormArray.push(this.formBuilder.group({
                id: [undefined],
                name: [authName, Validators.required],
                type: [getObjectProperty(s, 'type'), Validators.required],
                description: [getObjectProperty(s, 'description')],
                api_key_param_name: [getObjectProperty(s, 'name'), [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'apiKey')? Validators.required(c): null}]],
                api_key_in: [getObjectProperty(s, 'in'), [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'apiKey')? Validators.required(c): null}]],
                oauth2_grant_type: [this.mapOauth2SwaggerFlowTypes(getObjectProperty(s, 'flow')), [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'oauth2')? Validators.required(c): null}]],
                oauth2_authorization_url: [getObjectProperty(s, 'authorizationUrl'), [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'implicit' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]],
                oauth2_token_url: [getObjectProperty(s, 'tokenUrl'), [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'password' || c.parent.get('oauth2_grant_type').value == 'clientCredentials' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]],
                oauth2_scopes: this.getScopesFormArray(getObjectProperty(s, 'scopes')),
                oauth2_client_id: [null, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2')? Validators.required(c): null;
                }]],
                oauth2_client_secret: [null, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'password' || c.parent.get('oauth2_grant_type').value == 'clientCredentials' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]]
            }));
        });

        return authsFormArray;
    }

    getHeadersFormArray(headers: any): FormArray {
        let headersFormArray: FormArray = this.formBuilder.array([]);
        Object.keys(headers).forEach((headerName) => {
            if (!headerName.startsWith('x-')) {
                let defaultValueStr = getObjectProperty(headers[headerName], 'default');
                if (defaultValueStr && typeof defaultValueStr == 'object') {
                    defaultValueStr = JSON.stringify(defaultValueStr);
                }
                headersFormArray.push(this.formBuilder.group({
                    name: [headerName, Validators.required],
                    type: [getObjectProperty(headers[headerName], 'type'), Validators.required],
                    format: [getObjectProperty(headers[headerName], 'format')],
                    items: this.getItemsObjectForm(getObjectProperty(headers[headerName], 'items')),
                    items_join_separator: [getObjectProperty(headers[headerName], 'collectionFormat') || 'csv'],
                    default_value: [defaultValueStr]
                }));
            }
        });
        return headersFormArray;
    }

    getItemsObjectForm(items: any) {
        if (!items) {
            return [null];
        }
        if (items.type == 'array') {
            let defaultValueStr = getObjectProperty(items, 'default');
                if (defaultValueStr && typeof defaultValueStr == 'object') {
                    defaultValueStr = JSON.stringify(defaultValueStr);
                }
            return this.formBuilder.group({
                type: ['array', Validators.required],
                format: [getObjectProperty(items, 'format')],
                items: this.getItemsObjectForm(getObjectProperty(items, 'items')),
                items_join_separator: [getObjectProperty(items, 'collectionFormat') || 'csv'],
                default_value: [defaultValueStr]
            });
        } else {
            return this.getPrimitiveTypeFormGroup(items);
        }
    }

    getPrimitiveTypeFormGroup(config, isRequired = false) {
        let defaultValueStr = config.default;
        if (defaultValueStr && typeof defaultValueStr == 'object') {
            defaultValueStr = JSON.stringify(defaultValueStr);
        }
        let controlsConfig: any = {
            type: [config.type, Validators.required],
            format: [config.format],
            description: [config.description],
            default_value: [defaultValueStr],
            enum: [config.enum],
            example: [config.example]
        };
        if (isRequired) {
            controlsConfig.required = [1];
        }

        return this.formBuilder.group(controlsConfig);
    }
    getArrayTypeFormGroup(config, isRequired = false) {
        let controlsConfig: any = {
            type: ['array', Validators.required],
            items: this.getSchemaForm(config.items, isRequired)
        }
        if (isRequired) {
            controlsConfig.required = [1];
        }
        return this.formBuilder.group(controlsConfig);
    }
    getObjectFormGroup(config, isRequired = false) {
        let requiredProperties: string[] = config.required || [];
        let propertiesFormArray: FormArray = this.formBuilder.array([]);
        Object.keys(config.properties || {}).forEach((propertyName) => {
            propertiesFormArray.push(this.formBuilder.group({
                name: [propertyName, Validators.required],
                schema: this.getSchemaForm(config.properties[propertyName], requiredProperties.includes(propertyName))
            }));
        });
        let controlsConfig: any = {
            type: ['object', Validators.required],
            properties: propertiesFormArray
        };
        if (isRequired) {
            controlsConfig.required = [1];
        }
        return this.formBuilder.group(controlsConfig);
    }
    getSchemaForm(schema: any, isRequired = false) {
        if (!schema) {
            return null;
        }

        let type: string = getObjectProperty(schema, 'type');
        if (type == 'object') {
            return this.getObjectFormGroup(schema, isRequired);
        } else if (type == 'array') {
            return this.getArrayTypeFormGroup(schema, isRequired);
        } else {
            return this.getPrimitiveTypeFormGroup(schema, isRequired);
        }
    }

    getResponsesFormArray(operation: any): FormArray {
        let responsesFormArray: FormArray = this.formBuilder.array([]);
        Object.keys(getObjectProperty(operation, 'responses') || {}).forEach((responseCode) => {
            if (!responseCode.startsWith('x-')) {
                responsesFormArray.push(this.formBuilder.group({
                    id: [undefined],
                    code: [responseCode, Validators.required],
                    description: [getObjectProperty(operation.responses[responseCode], 'description'), Validators.required],
                    headers: this.getHeadersFormArray(getObjectProperty(operation.responses[responseCode], 'headers') || {}),
                    schema: this.getSchemaForm(getObjectProperty(operation.responses[responseCode], 'schema')) || [],
                    examples: [getObjectProperty(operation.responses[responseCode], 'examples')],
                }));
            }
        });

        return responsesFormArray;
    }

    getParametersFormArray(operation: any): FormArray {
        let parametersFormArray: FormArray = this.formBuilder.array([]);
        (<any[]>getObjectProperty(operation, 'parameters') || []).forEach((p) => {
            let defaultValueStr = getObjectProperty(p, 'default');
            if (defaultValueStr && typeof defaultValueStr == 'object') {
                defaultValueStr = JSON.stringify(defaultValueStr);
            }
            parametersFormArray.push(this.formBuilder.group({
                id: [undefined],
                name: [p.name, Validators.required],
                description: [getObjectProperty(p, 'description')],
                in: [getObjectProperty(p, 'in'), Validators.required],
                required: [(getObjectProperty(p, 'required') || false)? 1: 0],
                schema: this.getSchemaForm(getObjectProperty(p, 'schema'), (getObjectProperty(p, 'required') || false)) || [],
                type: [getObjectProperty(p, 'type'), [(c: AbstractControl) => { return (c.parent && c.parent.get('in').value != 'body')? Validators.required(c): null}]],
                format: [getObjectProperty(p, 'format')],
                allow_empty_value: [(getObjectProperty(p, 'allowEmptyValue') || false)? 1: 0],
                items: this.getItemsObjectForm(getObjectProperty(p, 'items')),
                items_join_separator: [getObjectProperty(p, 'collectionFormat')],
                default_value: [defaultValueStr],
                enum: [getObjectProperty(p, 'enum')]
            }));

        });

        return parametersFormArray;
    }

    getEndpointsFormArray(paths: any): FormArray {
        let endpointsFormArray: FormArray = this.formBuilder.array([]);
        Object.keys(paths).forEach((pathName) => {
            if (pathName != 'parameters' && pathName != '$ref' && !pathName.startsWith('x-')) {
                let path = paths[pathName];
                Object.keys(path).forEach((httpVerb) => {
                    if (httpVerb != 'parameters' && httpVerb != '$ref' && !httpVerb.startsWith('x-')) {
                        let currentOp = path[httpVerb];
                        
                        let authMethodsFormArray: FormArray = this.formBuilder.array([]);
                        let authMethods = <any[]>getObjectProperty(currentOp, 'security') || [];
                        authMethods.forEach((a) => {
                            let requiredAuthsFormArray: FormArray = this.formBuilder.array([]);
                            Object.keys(a).forEach((authName) => {
                                requiredAuthsFormArray.push(this.formBuilder.group({
                                    auth_name: [authName, Validators.required],
                                    scopes: [a[authName]]
                                }));
                            });
                            authMethodsFormArray.push(requiredAuthsFormArray);
                        });

                        endpointsFormArray.push(this.formBuilder.group({
                            id: [undefined],
                            url: [pathName, Validators.required],
                            http_verb: [httpVerb.toUpperCase(), Validators.required],
                            short_description: [getObjectProperty(currentOp, 'summary')],
                            long_description: [getObjectProperty(currentOp, 'description')],
                            operation_key: [getObjectProperty(currentOp, 'operationId')],
                            schemes: [getObjectProperty(currentOp, 'schemes')],
                            accepts: [getObjectProperty(currentOp, 'consumes')],
                            returns: [getObjectProperty(currentOp, 'produces'), Validators.required],
                            tags: [getObjectProperty(currentOp, 'tags')],
                            auth_methods: authMethodsFormArray,
                            responses: this.getResponsesFormArray(currentOp),
                            parameters: this.getParametersFormArray(currentOp),
                        }));    
                    }
                });
            }
        });

        return endpointsFormArray;
    }

    getFormGroupFromSpec(spec: any) : FormGroup | null {
        
        return this.formBuilder.group({
            id: [undefined],
            name: [getObjectProperty(spec, 'info.title'), Validators.required],
            description: [getObjectProperty(spec, 'info.description')],
            schemes: [getObjectProperty(spec, 'schemes') || ['https'], Validators.required],
            host: [getObjectProperty(spec, 'host'), Validators.required],
            base_path: [getObjectProperty(spec, 'basePath')],
            auths: this.getAuthsFormArray(getObjectProperty(spec, 'securityDefinitions') || {}),
            endpoints: [this.getEndpointsFormArray(getObjectProperty(spec, 'paths') || {}).value]
        });
    }
}